import { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';

const aboutItems = [
  {
    id: 'story',
    title: 'Our Story',
    preview:
      "Years of working with founders, CEOs, investors, and business leaders, BPS realized that industries don't need more graduates; they need more industry-oriented professionals who can contribute to industry-level projects. That belief inspired FutureLeadX, a community built to nurture the next generation of leaders.",
    iconBg: 'bg-[#E8F1FF]',
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="h-10 w-10" aria-hidden="true">
        <path
          d="M8 12c4 0 8-2 12-2v28c-4 0-8 2-12 2V12z"
          fill="#93C5FD"
          opacity="0.9"
        />
        <path
          d="M40 12c-4 0-8-2-12-2v28c4 0 8 2 12 2V12z"
          fill="#60A5FA"
        />
        <path d="M20 10v28M28 10v28" stroke="#1E40AF" strokeWidth="1.5" opacity="0.35" />
        <path d="M14 16h6M34 16h-6M14 22h6M34 22h-6" stroke="#1E3A8A" strokeWidth="1.2" opacity="0.4" />
      </svg>
    ),
    modal: {
      heading: 'Building Businesses to Building Careers',
      paragraphs: [
        { text: 'Every successful organization begins with a vision.', bold: false },
        {
          text: 'Eight years ago, Mr. Pankaj Anand and Mr. Bharat Sharma founded BPS Group with a clear vision—to empower businesses through world-class finance, advisory, and business solutions. What began as a bold ambition has grown into a globally trusted organization, partnering with 100+ businesses across the world. Driven by innovation, deep industry expertise, and an unwavering commitment to client success, BPS Group has helped organizations unlock growth, improve performance, and build lasting value.',
          bold: false,
        },
        { text: 'But as the business grew, one question became impossible to ignore.', bold: false },
        { text: 'Why were companies still struggling to find industry-ready talent?', bold: true },
        {
          text: 'Degrees were increasing. Certifications were everywhere. Yet employers continued searching for professionals who could think critically, solve real business problems, and contribute from day one.',
          bold: false,
        },
        { text: "The gap wasn't in education.", bold: true },
        { text: 'The gap was between learning and employability.', bold: true },
        {
          text: 'Determined to bridge this gap, Mr. Pankaj Anand and Mr. Bharat Sharma joined forces with Keshav Sharma, a strategic advisor with extensive experience working across multiple EdTech ventures.',
          bold: false,
        },
        { text: "And that's how FutureLeadX was born.", bold: true },
        {
          text: "FutureLeadX is more than an EdTech platform. It is the next chapter of BPS's journey—a place where learning meets industry, knowledge becomes capability, and ambition turns into opportunity.",
          bold: false,
        },
        {
          text: "Our programs are built to equip students, graduates, and young professionals with practical skills, real-world exposure, expert mentorship, and the confidence to thrive in today's competitive workplace.",
          bold: false,
        },
        { text: "Because education shouldn't stop at earning a certificate.", bold: true },
      ],
      highlight:
        'It should build careers, create future leaders, and empower the next generation to shape the future.',
    },
  },
  {
    id: 'mission',
    title: 'Our Mission',
    preview:
      'To build a lifelong community where ambitious individuals learn, lead, innovate, and grow together, supported by mentorship, real-world opportunities, global exposure, and a shared commitment to creating meaningful impact.',
    iconBg: 'bg-[#FFF0F3]',
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="h-10 w-10" aria-hidden="true">
        <circle cx="22" cy="24" r="12" fill="#FDA4AF" opacity="0.85" />
        <circle cx="22" cy="24" r="8" fill="#FB7185" />
        <circle cx="22" cy="24" r="4" fill="#BE123C" />
        <circle cx="22" cy="24" r="1.5" fill="#FFFFFF" />
        <path
          d="M28 18l12-6-2 8-4 2-6-4z"
          fill="#3B82F6"
        />
        <path d="M28 18l6 4" stroke="#1E40AF" strokeWidth="1" opacity="0.4" />
      </svg>
    ),
    modal: {
      heading: null,
      paragraphs: [
        {
          text: "We're building more than an education platform, we're building a community of future leaders.",
          bold: true,
        },
        {
          text: 'A community where ambitious individuals discover their purpose, learn from industry experts, gain real-world experience, and grow alongside like-minded peers who inspire them to think bigger.',
          bold: false,
        },
        {
          text: 'Our mission is to create lifelong opportunities through mentorship, global exposure, innovation, entrepreneurship, and access to the right ecosystem—empowering every learner to become a confident professional, a visionary leader, and, one day, a mentor for the next generation.',
          bold: false,
        },
        {
          text: "Because at FutureLeadX, your journey doesn't end with a course or a placement.",
          bold: true,
        },
      ],
      highlight:
        'It begins with a community that grows with you, celebrates your success, and helps you create an impact that extends far beyond your career.',
    },
  },
  {
    id: 'vision',
    title: 'Our Vision',
    preview:
      'To redefine how the world prepares talent. We aspire to create an ecosystem where education mirrors the realities of the workplace, practical experience becomes the foundation of learning, and every learner steps into the professional world with the confidence, competence, and mindset to make an impact from day one.',
    iconBg: 'bg-[#F3EEFF]',
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="h-10 w-10" aria-hidden="true">
        <path d="M24 10l10 6v8c0 6-4.5 11-10 13-5.5-2-10-7-10-13v-8l10-6z" fill="#A78BFA" opacity="0.35" />
        <circle cx="24" cy="20" r="7" fill="#8B5CF6" />
        <circle cx="24" cy="20" r="3.5" fill="#C4B5FD" />
        <circle cx="24" cy="20" r="1.5" fill="#FFFFFF" />
        <path d="M24 27v8M18 38h12M20 35h8" stroke="#6D28D9" strokeWidth="2" strokeLinecap="round" />
        <path d="M30 14l8-4" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round" />
        <circle cx="39" cy="9" r="2" fill="#7C3AED" />
      </svg>
    ),
    modal: {
      heading: null,
      paragraphs: [
        { text: 'To redefine how the world prepares talent.', bold: true },
        {
          text: 'We aspire to create an ecosystem where education mirrors the realities of the workplace, practical experience becomes the foundation of learning, and every learner steps into the professional world with the confidence, competence, and mindset to make an impact from day one.',
          bold: false,
        },
        { text: "Because the future doesn't need more degrees.", bold: true },
      ],
      highlight: 'It needs more professionals who are ready to deliver.',
    },
  },
];

function AboutModal({ item, onClose }) {
  const titleId = useId();

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <button
        type="button"
        className="absolute inset-0 bg-brand-navy/45 backdrop-blur-sm"
        aria-label="Close dialog"
        onClick={onClose}
      />

      <div className="relative flex max-h-[min(85vh,720px)] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-brand-white shadow-[0_24px_80px_-20px_rgba(17,22,44,0.45)] sm:max-w-xl">
        <div className="flex shrink-0 items-center justify-between gap-4 border-b border-brand-border/60 px-5 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <div
              className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${item.iconBg}`}
            >
              <span className="scale-90">{item.icon}</span>
            </div>
            <h3 id={titleId} className="text-card-title text-brand-navy">
              {item.title}
            </h3>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#E8F1FF] text-brand-secondary transition-colors hover:bg-brand-background hover:text-brand-navy"
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4" aria-hidden="true">
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="about-modal-scroll overflow-y-auto px-5 py-5 sm:px-6 sm:py-6">
          {item.modal.heading && (
            <h4 className="mb-5 text-lg font-semibold leading-snug text-brand-navy sm:text-xl">
              {item.modal.heading}
            </h4>
          )}

          <div className="space-y-4">
            {item.modal.paragraphs.map((paragraph) => (
              <p
                key={paragraph.text}
                className={`text-body-sm leading-relaxed text-brand-secondary ${
                  paragraph.bold ? 'font-semibold text-brand-navy' : ''
                }`}
              >
                {paragraph.text}
              </p>
            ))}
          </div>

          {item.modal.highlight && (
            <div className="mt-6 rounded-xl border border-brand-primary/15 border-l-4 border-l-brand-primary bg-[#F0F6FF] px-4 py-4">
              <p className="text-body-sm font-semibold leading-relaxed text-brand-primary">
                {item.modal.highlight}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AboutUs() {
  const [activeId, setActiveId] = useState(null);
  const activeItem = aboutItems.find((item) => item.id === activeId) ?? null;

  return (
    <section id="about" className="relative overflow-hidden bg-[#F5F7FA] py-10 lg:py-16">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-brand-white to-transparent" />

      <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
        <div className="mx-auto mb-10 max-w-2xl text-center lg:mb-14">
          <p className="mb-3 text-label font-semibold uppercase tracking-[0.2em] text-[#F97316]">
            About Us
          </p>
          <h2 className="text-section leading-tight tracking-wide text-brand-navy sm:text-[36px]">
            Who{' '}
            <span className="text-brand-primary">We Are</span>
          </h2>
          <div className="mx-auto mt-4 h-1 w-16 rounded-full bg-brand-primary" />
        </div>

        <div className="grid gap-6 md:grid-cols-3 lg:gap-8">
          {aboutItems.map((item) => (
            <article
              key={item.id}
              className="flex flex-col rounded-2xl bg-brand-white p-6 shadow-[0_12px_40px_-20px_rgba(17,22,44,0.25)] transition-transform duration-300 hover:-translate-y-1 sm:p-7"
            >
              <div
                className={`mb-5 flex h-14 w-14 items-center justify-center rounded-2xl ${item.iconBg}`}
              >
                {item.icon}
              </div>

              <h3 className="mb-3 text-card-title text-brand-navy">{item.title}</h3>

              <p className="mb-6 flex-1 text-body-sm leading-relaxed text-brand-secondary">
                {item.preview}
              </p>

              <button
                type="button"
                onClick={() => setActiveId(item.id)}
                className="self-start text-body-sm font-semibold text-brand-primary transition-colors hover:text-brand-dark"
              >
                Read more →
              </button>
            </article>
          ))}
        </div>

        <div className="mt-10 flex justify-center lg:mt-12">
          <Link
            to="/about"
            className="btn-primary inline-flex items-center gap-2 px-7"
          >
            View More About Us
            <span aria-hidden="true">→</span>
          </Link>
        </div>
      </div>

      {activeItem && (
        <AboutModal item={activeItem} onClose={() => setActiveId(null)} />
      )}
    </section>
  );
}

export default AboutUs;
